/**
 * Who cannot see what, as the data model shows it.
 *
 * Access is set here, on entities and fields, and stored by the server as
 * rules on the real tables and columns behind them. The server also does the
 * working-out in the other direction — which roles an entity or field is
 * hidden from right now — so the page only draws what it is told.
 *
 * Only someone who can manage roles gets any of this; for everyone else the
 * hook stays empty and asks the server for nothing.
 */
import React from 'react';

import { buildApiUrl } from '../../config';
import { useCan } from '../../ui/PermissionsProvider';

export interface AccessRole {
  role_id: string;
  name: string;
  description?: string | null;
}

export interface FieldAccess {
  name: string;
  /** False when no database column sits behind the field. */
  manageable: boolean;
  is_key: boolean;
  hidden_role_ids: string[];
  partial_role_ids: string[];
  /** Other entities using the same column; hiding the field hides it there too. */
  shared_with: string[];
  /** role id → entities keeping this field readable while its own is hidden. */
  visible_through: Record<string, string[]>;
}

export interface EntityAccess {
  name: string;
  manageable: boolean;
  /** False when every column is shared, so hiding it alone would hide nothing. */
  separable: boolean;
  shares_tables_with: string[];
  hidden_role_ids: string[];
  partial_role_ids: string[];
  /** role id → fields hidden one by one, which showing the entity would un-hide. */
  remembered_fields: Record<string, string[]>;
  fields: FieldAccess[];
}

export interface OtherTableAccess {
  connector_id: string;
  connector_name: string;
  table_name: string;
  hidden_role_ids: string[];
}

export interface DataModelAccess {
  /** Whether the workspace applies these rules yet. */
  enforced: boolean;
  roles: AccessRole[];
  entities: EntityAccess[];
  other_tables: OtherTableAccess[];
  summary: Record<string, { entities: number; fields: number; tables: number }>;
}

export interface RoleChange {
  role_id: string;
  hidden: boolean;
}

function authHeader(): Record<string, string> {
  const token = localStorage.getItem('klaris_jwt');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

function tenantId(): string | null {
  try {
    return JSON.parse(localStorage.getItem('klaris_tenant') || 'null')?.tenant_id ?? null;
  } catch {
    return null;
  }
}

export function useDataModelAccess() {
  const canManage = useCan('manage_roles');
  const [access, setAccess] = React.useState<DataModelAccess | null>(null);
  const [error, setError] = React.useState<string | null>(null);

  const base = React.useMemo(() => {
    const id = tenantId();
    return id ? `/api/v1/tenants/${id}/roles/data-model-access` : null;
  }, []);

  const reload = React.useCallback(async () => {
    if (!canManage || !base) return;
    try {
      const r = await fetch(buildApiUrl(base), { headers: authHeader() });
      if (!r.ok) throw new Error('Could not load who can see what');
      setAccess(await r.json());
      setError(null);
    } catch (e: any) {
      setError(e.message || 'Could not load who can see what');
    }
  }, [canManage, base]);

  React.useEffect(() => { reload(); }, [reload]);

  /** Sends one change and takes the server's fresh picture as the result. */
  const put = React.useCallback(async (path: string, body: unknown) => {
    if (!base) return;
    const r = await fetch(buildApiUrl(`${base}/${path}`), {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', ...authHeader() },
      body: JSON.stringify(body),
    });
    if (!r.ok) {
      const detail = (await r.json().catch(() => ({}))).detail;
      throw new Error(typeof detail === 'string' ? detail : 'Could not save');
    }
    setAccess(await r.json());
  }, [base]);

  const setEntity = React.useCallback(
    (entity: string, changes: RoleChange[]) => put('entity', { entity, changes }),
    [put],
  );
  const setFields = React.useCallback(
    (entity: string, fields: string[], changes: RoleChange[]) =>
      put('fields', { entity, fields, changes }),
    [put],
  );
  const setTable = React.useCallback(
    (connector_id: string, table_name: string, changes: RoleChange[]) =>
      put('table', { connector_id, table_name, changes }),
    [put],
  );

  return {
    canManage,
    access,
    /** Nothing to show yet, and something is on its way. */
    loading: canManage && !access && !error,
    error,
    reload,
    setEntity,
    setFields,
    setTable,
  };
}

/** "Sourcing", "Sourcing and Finance", "Sourcing, Finance and Ops". */
export function joinNames(names: string[]): string {
  if (names.length <= 1) return names.join('');
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
}

/** What a role cannot see, in one line, for the Roles page and role pickers. */
export function describeHidden(
  counts: { entities: number; fields: number; tables: number } | undefined,
): string {
  if (!counts) return 'Sees everything';
  const parts = [
    counts.entities && `${counts.entities} ${counts.entities === 1 ? 'entity' : 'entities'}`,
    counts.fields && `${counts.fields} ${counts.fields === 1 ? 'field' : 'fields'}`,
    counts.tables && `${counts.tables} other ${counts.tables === 1 ? 'table' : 'tables'}`,
  ].filter(Boolean) as string[];
  return parts.length ? `${joinNames(parts)} hidden` : 'Sees everything';
}
